import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User,
  Phone,
  Calendar,
  Activity,
  AlertCircle,
  FileText,
  Stethoscope,
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';

interface AppointmentWithPatientData {
  id: string;
  patient_id: string;
  date: string;
  time: string;
  reason: string;
  status: 'scheduled' | 'checked_in' | 'completed' | 'cancelled' | 'no_show' | 'missed' | 'in_progress';
  visit_type?: 'consultation' | 'follow_up' | 'check_up' | 'emergency';
  notes?: string;
  created_at: string;
  updated_at: string;
  patientName: string;
  patientAge: number;
  patientGender: string;
  patientPhone: string;
}

interface PatientProfileModalProps {
  patient: AppointmentWithPatientData | null;
  isOpen: boolean;
  onClose: () => void;
  onStartConsultation?: (appointment: AppointmentWithPatientData) => void;
}

export default function PatientProfileModal({ 
  patient, 
  isOpen, 
  onClose, 
  onStartConsultation 
}: Readonly<PatientProfileModalProps>) {
  const navigate = useNavigate();

  if (!isOpen || !patient) return null;

  const handleStartConsultation = () => {
    if (onStartConsultation) {
      onStartConsultation(patient);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="medical-card w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-xl mx-4">
        <div className="modal-header-professional bg-gradient-to-r from-info/5 to-info/10 border-b border-border">
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-info/10 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-info"/>
              </div>
              <div>
                <h3 className="text-base font-semibold text-foreground">Patient Profile</h3>
                <p className="text-xs text-muted-foreground">Quick view and essential information</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted/80 rounded-lg text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-4 h-4"/>
            </button>
          </div>
        </div>

        <div className="p-4 overflow-y-auto max-h-[calc(90vh-160px)]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Patient Information */}
            <div className="medical-section">
              <h4 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
                <User className="w-4 h-4 text-primary"/>
                Patient Information
              </h4>
                              <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                      <span className="text-lg font-bold text-primary">
                        {patient.patientName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <h4 className="text-base font-semibold text-foreground">{patient.patientName}</h4>
                      <p className="text-sm text-muted-foreground">{patient.patientAge} years • {patient.patientGender}</p>
                    </div>
                  </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-muted-foreground"/>
                    <span className="text-foreground">{patient.patientPhone}</span>
                    <button
                      onClick={() => window.open(`tel:${patient.patientPhone}`, '_self')}
                      className="text-primary hover:underline text-sm"
                    >
                      Call
                    </button>
                  </div>
                  <div className="flex items-center gap-3">
                    <Calendar className="w-4 h-4 text-muted-foreground"/>
                    <span className="text-foreground">Patient since: Nov 2023</span>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-border">
            <Button
              onClick={() => {
                onClose();
                navigate(`/patients/${patient.patient_id}`);
              }}
              className="medical-button-primary flex items-center gap-1 px-3 py-2 text-sm"
            >
              <User className="w-3 h-3"/>
              Full Profile
            </Button>

            <Button
              onClick={handleStartConsultation}
              className="medical-button-outline flex items-center gap-1 px-3 py-2 text-sm"
            >
              <Stethoscope className="w-3 h-3"/>
              Start Prescription
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
} 